// src/features/task/inheritance.ts
// Pure logic behind "what will actually happen here, and who decided it".
//
// ResolveTaskContext answers the second half: every resolved value carries a
// TaskConfigOrigin naming the record that supplied it and the scope that record
// is attached to. Three shapes of that scope mean three different things to a
// person about to go and edit it, and they are not interchangeable:
//
//   scope_entity == the scope we asked about  → set right here
//   scope_entity != the scope we asked about  → set further up the chain
//   scope_entity absent, scope_entity_type set → governs *every* scope of that
//        kind, so editing it changes the answer for all of them
//
// The third case is the one worth being loud about: it is the only one where
// opening the record and changing it affects people who never looked at this
// screen. Everything here is framework-agnostic — no React, no i18n lookups,
// only the key a caller should translate.

import type { Entity } from '@core/types/entity';
import type {
  ResolvedReference,
  ResolvedStatusSet,
  TaskConfigOrigin,
  TaskConfigSource,
} from '@core/types/task';

/** Which of the three scope shapes an origin has. */
export type OriginScopeCase = 'here' | 'ancestor' | 'kind' | 'unknown';

/** i18n leaf under task.inheritance.source for each backend source enum. */
const SOURCE_KEY: Record<string, string> = {
  TASK_CONFIG_SOURCE_TASK_TYPE: 'taskType',
  TASK_CONFIG_SOURCE_POLICY: 'policy',
  TASK_CONFIG_SOURCE_CONTAINER_CONFIG: 'containerConfig',
  TASK_CONFIG_SOURCE_CATEGORY: 'category',
};

/**
 * The scope kinds the platform attaches configuration to, and so the ones with
 * a translated noun. Anything else is shown as the backend spelled it rather
 * than guessed at — a wrong noun in this sentence would mislead about reach.
 */
const KNOWN_SCOPE_KINDS = new Set([
  'project',
  'team',
  'department',
  'tenant',
  'category',
  'pipeline',
]);

/** i18n leaf under task.inheritance.source; 'unknown' when the enum is absent. */
export function originSourceKey(source: TaskConfigSource | undefined): string {
  return (source && SOURCE_KEY[source]) || 'unknown';
}

/**
 * Which sentence describes this origin's scope.
 *
 * An origin with a scope entity is compared by id against the scope the form
 * asked about: same id means the record sits on this scope, a different id
 * means it sits somewhere above it. No scope entity but a scope *type* is the
 * blanket case — one record governing every scope of that kind.
 */
export function originScopeCase(
  origin: TaskConfigOrigin | undefined,
  askedScope: Entity | undefined,
): OriginScopeCase {
  if (!origin) return 'unknown';
  const scopeId = origin.scope_entity?.id;
  if (scopeId) {
    if (askedScope?.id && scopeId === askedScope.id) return 'here';
    return 'ancestor';
  }
  if (origin.scope_entity_type) return 'kind';
  return 'unknown';
}

/**
 * The noun for the scope in the sentence ("project", "team", …). Prefers the
 * entity's own type over the record's declared scope type, because for an
 * ancestor origin the entity is the thing being talked about.
 */
export function originScopeKind(origin: TaskConfigOrigin | undefined): string {
  if (!origin) return '';
  return origin.scope_entity?.type_name || origin.scope_entity_type || '';
}

/** True when a scope kind has a translated noun; otherwise show it verbatim. */
export function isKnownScopeKind(kind: string): boolean {
  return KNOWN_SCOPE_KINDS.has(kind);
}

/**
 * Where a person goes to change this record.
 *
 * Policies, container configs and categories live in tabs of the task admin
 * page, which has no route of its own per tab — the query carries the intent
 * so the link stays honest about what it is pointing at, and `record` names
 * the row. A task-type origin has a real deep link.
 */
export function originAdminRoute(origin: TaskConfigOrigin | undefined): string | null {
  const id = origin?.origin?.id || '';
  switch (origin?.source) {
    case 'TASK_CONFIG_SOURCE_POLICY':
      return `/settings/tasks?tab=policies${id ? `&record=${encodeURIComponent(id)}` : ''}`;
    case 'TASK_CONFIG_SOURCE_CONTAINER_CONFIG':
      return `/settings/tasks?tab=containers${id ? `&record=${encodeURIComponent(id)}` : ''}`;
    case 'TASK_CONFIG_SOURCE_CATEGORY':
      return `/settings/tasks?tab=categories${id ? `&record=${encodeURIComponent(id)}` : ''}`;
    case 'TASK_CONFIG_SOURCE_TASK_TYPE':
      return id ? `/settings/task-types/${encodeURIComponent(id)}` : null;
    default:
      return null;
  }
}

/**
 * True when the resolver handed back the very type being edited.
 *
 * These screens resolve the scope *without* a task_type_id, so what comes back
 * is what the scope offers on its own — the thing a type with no value of its
 * own would follow, and the thing a type with one replaces. If the backend
 * nonetheless names this type as the source, that is not inheritance and must
 * not be worded as such.
 */
export function isSelfOrigin(
  origin: TaskConfigOrigin | undefined,
  taskTypeId: string | undefined,
): boolean {
  if (!origin || !taskTypeId) return false;
  return origin.source === 'TASK_CONFIG_SOURCE_TASK_TYPE' && origin.origin?.id === taskTypeId;
}

/** The entity id a resolved single-value reference points at, or ''. */
export function referenceId(ref: ResolvedReference | undefined): string {
  return ref?.reference?.id || '';
}

/**
 * The inherited status set, or null when the scope offers none.
 *
 * An empty `status_definitions` is not an inherited set — it is the absence of
 * one, and reads as "nothing configured anywhere" rather than as a set to
 * follow or to copy.
 */
export function inheritedStatusSet(
  resolved: ResolvedStatusSet | undefined,
  taskTypeId?: string,
): ResolvedStatusSet | null {
  if (!resolved) return null;
  if (!resolved.status_definitions || resolved.status_definitions.length === 0) return null;
  if (isSelfOrigin(resolved.origin, taskTypeId)) return null;
  return resolved;
}

/**
 * The inherited single value, or null when the scope offers none.
 * Same rule as above: a reference with no id is an absence, not a value.
 */
export function inheritedReference(
  resolved: ResolvedReference | undefined,
  taskTypeId?: string,
): ResolvedReference | null {
  if (!resolved || !referenceId(resolved)) return null;
  if (isSelfOrigin(resolved.origin, taskTypeId)) return null;
  return resolved;
}
